import React from 'react';
import type { GroupAggregate, GroupBy, PropertyGroup } from '../../pages/property-grouping';
import { GroupSummaryCard } from './GroupSummaryCard';

interface GroupSummaryGridProps {
  /** The active grouping dimension (hides the split when grouping by business). */
  by: Exclude<GroupBy, 'none'>;
  /** The groups to render as cards, already ordered. */
  groups: PropertyGroup[];
  /** Per-group aggregate stats; null when still pending. */
  aggregates: Map<string, GroupAggregate | null>;
  /** The portfolio total, for each card's share percentage. */
  total: number;
  /** Applies a group's filter and returns to the list view. */
  onSelectGroup: (group: PropertyGroup) => void;
}

/**
 * The Resumo presentation: a responsive grid of {@link GroupSummaryCard}, one
 * per group, rendering the same aggregates the table header does. A card click
 * narrows the list to that group and switches back to Lista.
 *
 * @param props - The groups, aggregates, portfolio total, and select handler.
 * @returns The summary grid element.
 */
export const GroupSummaryGrid: React.FC<GroupSummaryGridProps> = ({ by, groups, aggregates, total, onSelectGroup }) => {
  return (
    <div
      data-testid="group-summary-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 'var(--space-4)',
        padding: 'var(--space-1) 0 var(--space-4)',
      }}
    >
      {groups.map((group) => (
        <GroupSummaryCard
          key={group.key}
          group={group}
          aggregate={aggregates.has(group.key) ? aggregates.get(group.key)! : null}
          total={total}
          showSplit={by !== 'business'}
          onSelect={() => onSelectGroup(group)}
        />
      ))}
    </div>
  );
};
